import { compact, filter, find, first, map, size, uniq } from 'es-toolkit/compat';
import moment from 'moment';

import { FeatureMetadata } from 'components/Procedure/useSlideChannelsAndResults/featureMetadata';
import { getPrimaryRunOrchestrationIdFromExperimentResult } from 'components/Procedure/useSlideChannelsAndResults/secondaryAnalysis';
import { ExperimentResult, FlowClassName } from 'interfaces/experimentResults';
import { CalculateFeaturesJob } from 'interfaces/job';

export const getPublishedPrimaryRunOrchestrationId = ({
  experimentResults,
  slideId,
  studyId,
}: {
  experimentResults: ExperimentResult[];
  slideId: string;
  studyId: string;
}) => {
  const publishedCalculateFeaturesExperimentResults = filter(
    experimentResults,
    (result) =>
      result.flowClassName === FlowClassName.CalculateFeatures &&
      result.approved &&
      result.orchestrationId &&
      !result.deletedAt
  );

  if (size(publishedCalculateFeaturesExperimentResults) > 1) {
    console.warn(
      `Found multiple published CalculateFeatures results for slide ${slideId} study ID ${studyId}: ${map(
        publishedCalculateFeaturesExperimentResults,
        ({ experimentResultId, orchestrationId, createdAt }) =>
          JSON.stringify({ experimentResultId, orchestrationId, createdAt })
      )}`
    );
  }

  const publishedPrimaryRunOrchestrationId = first(
    uniq(
      compact(
        map(
          publishedCalculateFeaturesExperimentResults,
          (result) => result.primaryRunOrchestrationId || result.orchestrationId
        )
      )
    )
  );

  return publishedPrimaryRunOrchestrationId;
};

export const getExperimentResultLabel = ({
  result,
  canSeeOrchestrationId,
  showDeletedBy,
  primaryJobName,
}: {
  result: Pick<
    ExperimentResult,
    'primaryRunOrchestrationId' | 'createdAt' | 'orchestrationId' | 'deletedAt' | 'deletedBy' | 'approved'
  >;
  canSeeOrchestrationId: boolean;
  showDeletedBy: boolean;
  primaryJobName?: string;
}) => {
  const createdAt = moment(result?.createdAt).format('YYYY-MM-DD HH:mm');
  const orchestrationId = canSeeOrchestrationId && result?.orchestrationId;
  const deletedAt = result?.deletedAt ? moment(result?.deletedAt).format('YYYY-MM-DD HH:mm') : undefined;
  const displayName = primaryJobName;
  const resultPrimaryRunOrchestrationId = getPrimaryRunOrchestrationIdFromExperimentResult(result);
  return `${createdAt}${!resultPrimaryRunOrchestrationId ? ' (Primary)' : ''}${result?.approved ? ' (Approved)' : ''}${
    orchestrationId ? ` - ${orchestrationId}` : ''
  }${displayName ? ` - ${displayName}` : ''}${
    deletedAt ? ` (hidden on ${deletedAt}${showDeletedBy && result?.deletedBy ? ` by ${result?.deletedBy}` : ''})` : ''
  }`;
};

export const getPublishedHeatmapsOrchestrationId = ({
  activePublishedOrchestrationId,
  activeResult,
}: {
  activePublishedOrchestrationId?: string;
  activeResult?: Pick<FeatureMetadata, 'primaryRunOrchestrationId' | 'flowClassName' | 'job'>;
}) => getPrimaryRunOrchestrationIdFromExperimentResult(activeResult) || activePublishedOrchestrationId;

export const getActiveSecondaryAnalysisJob = <T extends Pick<CalculateFeaturesJob, 'orchestrationId'>>({
  secondaryAnalysisJobsForSlide,
  activeResult,
}: {
  secondaryAnalysisJobsForSlide: T[];
  activeResult?: Pick<FeatureMetadata, 'orchestrationId' | 'primaryRunOrchestrationId' | 'flowClassName' | 'job'>;
}): T | undefined => {
  if (!getPrimaryRunOrchestrationIdFromExperimentResult(activeResult) || !activeResult?.orchestrationId) {
    return undefined;
  }

  const activeResultJobId = activeResult?.job?.id;

  return find(
    secondaryAnalysisJobsForSlide,
    (job) =>
      job.orchestrationId === activeResult.orchestrationId ||
      Boolean(activeResultJobId && (job as Partial<CalculateFeaturesJob>)?.id === activeResultJobId)
  );
};
